import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Input, Select, Spinner } from "../../shared";
import { toast } from "react-toastify";
import {
  useCreateDashboardFromTemplateMutation,
  useDeleteDashboardMutation,
  useGetDashboardsQuery,
  useGetDashboardTemplatesQuery,
  useGetDefaultDashboardDefinitionQuery,
  useResolveDashboardMutation,
  useSetDefaultDashboardMutation,
  useUpdateDashboardMutation,
} from "../../redux/services/dashboards/dashboardBuilderApi";
import DashboardRenderer from "./components/DashboardRenderer";
import DashboardToolbar from "./components/DashboardToolbar";
import TemplatePicker from "./components/TemplatePicker";
import { useAuth } from "../../hooks";

const canPreviewTemplates = (authority) =>
  ["admin", "owner", "superAdmin"].includes(authority);

const getDashboardId = (dashboard) =>
  dashboard?._id || dashboard?.id || dashboard?.templateKey;

const scopeOptions = [
  { label: "Customer", value: "customer" },
  { label: "User", value: "user" },
  { label: "Team", value: "team" },
];

const isSavedDashboard = (dashboard) => dashboard && !dashboard.isSystemTemplate;

const DashboardV2 = () => {
  const { user } = useAuth();
  const [dateFilter, setDateFilter] = useState({ type: "thisWeek" });
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [dashboardName, setDashboardName] = useState("");
  const [copyScope, setCopyScope] = useState("customer");
  const [widgetDraft, setWidgetDraft] = useState([]);

  const {
    data: defaultDashboard,
    isLoading: defaultLoading,
    isError: defaultError,
  } = useGetDefaultDashboardDefinitionQuery();

  const {
    data: dashboards = [],
    isLoading: dashboardsLoading,
    refetch: refetchDashboards,
  } = useGetDashboardsQuery();

  const {
    data: templates = [],
    isLoading: templatesLoading,
  } = useGetDashboardTemplatesQuery(undefined, {
    skip: !canPreviewTemplates(user?.authority),
  });

  const [
    resolveDashboard,
    {
      data: resolvedDashboard,
      isLoading: resolveLoading,
      isError: resolveError,
    },
  ] = useResolveDashboardMutation();

  const [
    createDashboardFromTemplate,
    { isLoading: creatingDashboard },
  ] = useCreateDashboardFromTemplateMutation();
  const [updateDashboard, { isLoading: updatingDashboard }] =
    useUpdateDashboardMutation();
  const [deleteDashboard, { isLoading: deletingDashboard }] =
    useDeleteDashboardMutation();
  const [setDefaultDashboard, { isLoading: settingDefault }] =
    useSetDefaultDashboardMutation();

  useEffect(() => {
    if (!activeDashboard && defaultDashboard) {
      setActiveDashboard(defaultDashboard);
    }
  }, [activeDashboard, defaultDashboard]);

  useEffect(() => {
    setDashboardName(activeDashboard?.name || "");
    setWidgetDraft(
      Array.isArray(activeDashboard?.widgets) ? activeDashboard.widgets : []
    );
  }, [activeDashboard]);

  const dashboardId = getDashboardId(activeDashboard);

  useEffect(() => {
    if (!dashboardId) return;
    resolveDashboard({
      id: dashboardId,
      filters: {
        dateRange: dateFilter,
      },
    });
  }, [dashboardId, dateFilter, resolveDashboard]);

  const activeTemplateKey = useMemo(
    () => activeDashboard?.templateKey || activeDashboard?.id,
    [activeDashboard]
  );

  const capabilities =
    resolvedDashboard?.capabilities || activeDashboard?.capabilities || {};

  const canCopySelectedTemplate =
    activeDashboard?.isSystemTemplate &&
    canPreviewTemplates(user?.authority) &&
    capabilities.customerDashboards;

  const canEditSelectedDashboard =
    isSavedDashboard(activeDashboard) && canPreviewTemplates(user?.authority);

  const savedDashboardOptions = useMemo(
    () =>
      dashboards.map((dashboard) => ({
        label: `${dashboard.name}${dashboard.isDefault ? " (Default)" : ""}`,
        value: getDashboardId(dashboard),
      })),
    [dashboards]
  );

  const selectedDashboardLabel = useMemo(() => {
    const activeId = getDashboardId(activeDashboard);
    return (
      savedDashboardOptions.find((option) => option.value === activeId)?.label ||
      activeDashboard?.name ||
      ""
    );
  }, [activeDashboard, savedDashboardOptions]);

  const availableScopeOptions = scopeOptions.filter((option) => {
    if (option.value === "customer") return capabilities.customerDashboards;
    if (option.value === "user") return capabilities.userDashboards;
    if (option.value === "team") return capabilities.teamDashboards;
    return false;
  });

  const handleDashboardSelect = (event) => {
    const selected = dashboards.find(
      (dashboard) => getDashboardId(dashboard) === event.value
    );
    if (selected) setActiveDashboard(selected);
  };

  const handleCopyTemplate = async () => {
    if (!activeDashboard?.templateKey) return;
    try {
      const created = await createDashboardFromTemplate({
        templateKey: activeDashboard.templateKey,
        name: dashboardName || activeDashboard.name,
        scope: copyScope,
      }).unwrap();
      setActiveDashboard(created);
      await refetchDashboards();
      toast("Dashboard created", {
        type: "success",
        toastId: "dashboard-created",
      });
    } catch (error) {
      toast(error?.data?.message || "Unable to create dashboard", {
        type: "error",
        toastId: "dashboard-create-error",
      });
    }
  };

  const handleSaveDashboard = async () => {
    if (!canEditSelectedDashboard) return;
    try {
      const updated = await updateDashboard({
        id: getDashboardId(activeDashboard),
        name: dashboardName,
        filters: {
          ...(activeDashboard.filters || {}),
          dateRange: dateFilter,
        },
        widgets: widgetDraft,
        layout: activeDashboard.layout || [],
      }).unwrap();
      setActiveDashboard(updated);
      await resolveDashboard({
        id: getDashboardId(updated),
        filters: {
          dateRange: dateFilter,
        },
      });
      await refetchDashboards();
      toast("Dashboard saved", {
        type: "success",
        toastId: "dashboard-saved",
      });
    } catch (error) {
      toast(error?.data?.message || "Unable to save dashboard", {
        type: "error",
        toastId: "dashboard-save-error",
      });
    }
  };

  const handleSetDefault = async () => {
    if (!canEditSelectedDashboard) return;
    try {
      const updated = await setDefaultDashboard(
        getDashboardId(activeDashboard)
      ).unwrap();
      setActiveDashboard(updated);
      await refetchDashboards();
      toast("Default dashboard updated", {
        type: "success",
        toastId: "dashboard-default-updated",
      });
    } catch (error) {
      toast(error?.data?.message || "Unable to set default dashboard", {
        type: "error",
        toastId: "dashboard-default-error",
      });
    }
  };

  const handleArchiveDashboard = async () => {
    if (!canEditSelectedDashboard) return;
    try {
      await deleteDashboard(getDashboardId(activeDashboard)).unwrap();
      await refetchDashboards();
      setActiveDashboard(defaultDashboard);
      toast("Dashboard archived", {
        type: "success",
        toastId: "dashboard-archived",
      });
    } catch (error) {
      toast(error?.data?.message || "Unable to archive dashboard", {
        type: "error",
        toastId: "dashboard-archive-error",
      });
    }
  };

  const toggleWidget = (widgetId) => {
    setWidgetDraft((current) =>
      current.map((widget) =>
        widget.widgetId === widgetId
          ? { ...widget, enabled: widget.enabled === false }
          : widget
      )
    );
  };

  if (defaultLoading) {
    return (
      <div className="flex h-full min-h-[420px] items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (defaultError) {
    return (
      <div className="p-3">
        <Card className="rounded-lg border-red-200">
          <div className="font-semibold text-red-600">
            Unable to load the dynamic dashboard.
          </div>
          <div className="mt-2 text-sm text-gray-500">
            The current dashboard remains available from the main Dashboard page.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <DashboardToolbar
        dashboard={activeDashboard}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
      />
      <div className="space-y-3 p-3">
        <Card className="rounded-lg">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            <Select
              id="dashboard"
              label="Dashboard"
              value={selectedDashboardLabel}
              options={savedDashboardOptions}
              loading={dashboardsLoading}
              onChange={handleDashboardSelect}
            />
            <Input
              id="dashboardName"
              label="Dashboard Name"
              value={dashboardName}
              onChange={(target) => setDashboardName(target.value)}
              disabled={!canEditSelectedDashboard && !activeDashboard?.isSystemTemplate}
            />
            {activeDashboard?.isSystemTemplate && (
              <Select
                id="copyScope"
                label="Copy Scope"
                value={
                  availableScopeOptions.find((option) => option.value === copyScope)
                    ?.label || ""
                }
                options={availableScopeOptions}
                onChange={(event) => setCopyScope(event.value)}
              />
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {activeDashboard?.isSystemTemplate && (
              <Button
                size="sm"
                loading={creatingDashboard}
                disabled={!canCopySelectedTemplate || creatingDashboard}
                onClick={handleCopyTemplate}
              >
                Use this template
              </Button>
            )}
            {canEditSelectedDashboard && (
              <>
                <Button
                  size="sm"
                  loading={updatingDashboard}
                  disabled={updatingDashboard}
                  onClick={handleSaveDashboard}
                >
                  Save dashboard
                </Button>
                <Button
                  size="sm"
                  type="alternative"
                  loading={settingDefault}
                  disabled={settingDefault || activeDashboard?.isDefault}
                  onClick={handleSetDefault}
                >
                  Set as default
                </Button>
                <Button
                  size="sm"
                  type="danger"
                  loading={deletingDashboard}
                  disabled={deletingDashboard}
                  onClick={handleArchiveDashboard}
                >
                  Archive
                </Button>
              </>
            )}
          </div>
          {canEditSelectedDashboard && widgetDraft.length > 0 && (
            <div className="mt-4">
              <div className="mb-2 text-xs font-bold uppercase text-gray-500">
                Visible Widgets
              </div>
              <div className="flex flex-wrap gap-2">
                {widgetDraft.map((widget) => (
                  <Button
                    key={widget.widgetId}
                    size="xs"
                    type={widget.enabled === false ? "alternative" : "primary"}
                    onClick={() => toggleWidget(widget.widgetId)}
                  >
                    {widget.enabled === false ? "Show" : "Hide"} {widget.title}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {!capabilities.customerDashboards && activeDashboard?.isSystemTemplate && (
            <p className="mt-3 text-sm text-gray-500">
              Your current plan can view system dashboards. Upgrade to Growth or
              higher to save custom dashboards.
            </p>
          )}
        </Card>
        {canPreviewTemplates(user?.authority) && (
          <TemplatePicker
            templates={templates}
            loading={templatesLoading}
            activeTemplateKey={activeTemplateKey}
            onSelectTemplate={setActiveDashboard}
          />
        )}
        {resolveError && (
          <Card className="rounded-lg border-red-200">
            <div className="font-semibold text-red-600">
              Unable to resolve dashboard data.
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Try a different date range or template.
            </div>
          </Card>
        )}
        <DashboardRenderer
          resolvedDashboard={resolvedDashboard}
          loading={resolveLoading}
        />
      </div>
    </div>
  );
};

export default DashboardV2;
